import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { LanguageService } from '../../../services/i18n/language';
import {
  CertificateDocumentTypesService,
  CertificateDocumentType
} from '../../../services/api/certificate-document-types';
import { Subscription } from 'rxjs';
import { ConfirmDialogService } from '../../../services/confirm-dialog';

@Component({
  selector: 'app-document-types',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './document-types.html',
  styleUrl: './document-types.scss'
})
export class DocumentTypesComponent implements OnInit, OnDestroy {
  /** Подтверждение на странице вместо системного confirm() — его блокируют встроенные браузеры. */
  private confirmDialog = inject(ConfirmDialogService);

  /** Встроенный режим (вкладка «Настройки») — без кнопки «Назад». */
  @Input() embedded = false;

  types: CertificateDocumentType[] = [];
  loading = false;
  error: string | null = null;

  createForm: FormGroup;
  creating = false;

  editingId: number | null = null;
  editForm: FormGroup;
  saving: Record<number, boolean> = {};
  deleting: Record<number, boolean> = {};

  backText = 'Назад';
  titleText = 'Типы разрешительных документов';
  hintText = 'Список документов/сертификатов, которые AI проверяет при определении требований по товару. AI вернёт пользователю только те документы из этого списка, которые реально нужны для конкретного товара.';
  namePlaceholderText = 'Название документа/сертификата';
  aiHintPlaceholderText = 'Подсказка для AI — когда документ требуется (необязательно)';
  addingText = 'Добавление…';
  addText = '+ Добавить';
  loadingText = 'Загрузка...';
  columnNameText = 'Название';
  columnAiHintText = 'Подсказка для AI';
  columnActionsText = 'Действия';
  editNamePlaceholderText = 'Название';
  editAiHintPlaceholderText = 'Подсказка для AI';
  saveText = 'Сохранить';
  cancelText = 'Отмена';
  editText = 'Изменить';
  deleteText = 'Удалить';
  emptyStateText = 'Типы документов не добавлены';
  errorLoadText = 'Не удалось загрузить список типов документов';
  errorCreateText = 'Не удалось создать тип документа';
  errorSaveText = 'Не удалось сохранить изменения';
  errorDeleteText = 'Не удалось удалить тип документа';
  deleteConfirmText = 'Удалить тип документа «{name}»?';

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private languageService: LanguageService,
    private documentTypesService: CertificateDocumentTypesService,
    private cdr: ChangeDetectorRef
  ) {
    this.createForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      hint: ['']
    });
    this.editForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      hint: ['']
    });
  }

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.backText = this.t('customsBroker.dashboard.settingsBack', 'Назад');
    this.titleText = this.t('customsBroker.dashboard.settingsDocumentTypes', 'Типы разрешительных документов');
    this.hintText = this.t('customsBroker.documentTypes.hint', 'Список документов/сертификатов, которые AI проверяет при определении требований по товару. AI вернёт пользователю только те документы из этого списка, которые реально нужны для конкретного товара.');
    this.namePlaceholderText = this.t('customsBroker.documentTypes.namePlaceholder', 'Название документа/сертификата');
    this.aiHintPlaceholderText = this.t('customsBroker.documentTypes.aiHintPlaceholder', 'Подсказка для AI — когда документ требуется (необязательно)');
    this.addingText = this.t('customsBroker.documentTypes.adding', 'Добавление…');
    this.addText = this.t('customsBroker.documentTypes.add', '+ Добавить');
    this.loadingText = this.t('common.loading', 'Загрузка...');
    this.columnNameText = this.t('customsBroker.documentTypes.columnName', 'Название');
    this.columnAiHintText = this.t('customsBroker.documentTypes.columnAiHint', 'Подсказка для AI');
    this.columnActionsText = this.t('customsBroker.documentTypes.columnActions', 'Действия');
    this.editNamePlaceholderText = this.t('customsBroker.documentTypes.columnName', 'Название');
    this.editAiHintPlaceholderText = this.t('customsBroker.documentTypes.columnAiHint', 'Подсказка для AI');
    this.saveText = this.t('customsBroker.documentTypes.save', 'Сохранить');
    this.cancelText = this.t('common.cancel', 'Отмена');
    this.editText = this.t('customsBroker.documentTypes.edit', 'Изменить');
    this.deleteText = this.t('common.delete', 'Удалить');
    this.emptyStateText = this.t('customsBroker.documentTypes.emptyState', 'Типы документов не добавлены');
    this.errorLoadText = this.t('customsBroker.documentTypes.errorLoad', 'Не удалось загрузить список типов документов');
    this.errorCreateText = this.t('customsBroker.documentTypes.errorCreate', 'Не удалось создать тип документа');
    this.errorSaveText = this.t('customsBroker.documentTypes.errorSave', 'Не удалось сохранить изменения');
    this.errorDeleteText = this.t('customsBroker.documentTypes.errorDelete', 'Не удалось удалить тип документа');
    this.deleteConfirmText = this.t('customsBroker.documentTypes.deleteConfirm', 'Удалить тип документа «{name}»?');
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.documentTypesService.getAll().subscribe({
      next: (types) => {
        this.types = types;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading document types:', err);
        this.error = this.errorLoadText;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onCreate(): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    this.creating = true;
    this.error = null;
    const value = this.createForm.value;
    this.documentTypesService.create({
      name: value.name.trim(),
      hint: value.hint?.trim() || undefined,
      active: true
    }).subscribe({
      next: (type) => {
        this.types = [...this.types, type];
        this.createForm.reset({ name: '', hint: '' });
        this.creating = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error creating document type:', err);
        this.error = err.error?.message || this.errorCreateText;
        this.creating = false;
        this.cdr.markForCheck();
      }
    });
  }

  startEdit(type: CertificateDocumentType): void {
    this.editingId = type.id;
    this.editForm.reset({
      name: type.name,
      hint: type.hint || ''
    });
  }

  cancelEdit(): void {
    this.editingId = null;
  }

  saveEdit(type: CertificateDocumentType): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }
    this.saving[type.id] = true;
    this.error = null;
    const value = this.editForm.value;
    this.documentTypesService.update(type.id, {
      name: value.name.trim(),
      hint: value.hint?.trim() || undefined,
      active: true,
      viewOrder: type.viewOrder
    }).subscribe({
      next: (updated) => {
        this.types = this.types.map(t => t.id === updated.id ? updated : t);
        this.saving[type.id] = false;
        this.editingId = null;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error updating document type:', err);
        this.error = err.error?.message || this.errorSaveText;
        this.saving[type.id] = false;
        this.cdr.markForCheck();
      }
    });
  }

  async deleteType(type: CertificateDocumentType): Promise<void> {
    if (!await this.confirmDialog.confirm(this.deleteConfirmText.replace('{name}', type.name))) return;
    this.deleting[type.id] = true;
    this.error = null;
    this.documentTypesService.delete(type.id).subscribe({
      next: () => {
        this.types = this.types.filter(t => t.id !== type.id);
        this.deleting[type.id] = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error deleting document type:', err);
        this.error = err.error?.message || this.errorDeleteText;
        this.deleting[type.id] = false;
        this.cdr.markForCheck();
      }
    });
  }
}
